// HoverTooltip — a rendered tooltip, used where the native `title` attribute is not
// good enough. The native tooltip's font size is decided by the OS/browser and cannot
// be styled at all, which made small numbers unreadable (WCO-2155 feedback).
//
// Two details that matter here:
//  1. It renders through a PORTAL with `position: fixed`. The packing tables live inside
//     an `overflow-x-auto` wrapper, which CLIPS absolutely-positioned children — an
//     in-flow tooltip would be cut off at the cell edge.
//  2. It opens on focus as well as hover, so it is reachable by keyboard.

import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface HoverTooltipProps {
  /** Tooltip text. Nothing renders when empty. */
  label: string;
  children: React.ReactNode;
  className?: string;
  /** Overrides the bubble's text styling (default: large, bold). */
  textClassName?: string;
  /** 'start' / 'end' anchor the bubble's left / right edge to the trigger instead of centring it. */
  align?: 'center' | 'start' | 'end';
}

const HoverTooltip: React.FC<HoverTooltipProps> = ({
  label, children, className = '', textClassName = 'text-base font-semibold', align = 'center',
}) => {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const show = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Anchored above the trigger and horizontally centred on it. Fixed coordinates are
    // viewport-relative, which is what lets this escape the scroll container.
    setCoords({ top: r.top, left: align === 'start' ? r.left : align === 'end' ? r.right : r.left + r.width / 2 });
  }, [align]);

  const hide = useCallback(() => setCoords(null), []);

  if (!label) return <>{children}</>;

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex items-center ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={0}
        role="button"
        aria-label={label}
      >
        {children}
      </span>
      {coords && createPortal(
        <div
          role="tooltip"
          className={`fixed z-[9999] ${align === 'center' ? '-translate-x-1/2' : align === 'end' ? '-translate-x-full' : ''} -translate-y-full pointer-events-none`}
          style={{ top: coords.top - 8, left: coords.left }}
        >
          <div className={`px-3 py-1.5 rounded-lg bg-gray-900 dark:bg-gray-700 text-white ${textClassName} whitespace-nowrap shadow-lg ring-1 ring-black/10`}>
            {label}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default HoverTooltip;
